import React from 'react';
import { NT, NI, NMButton } from './NaomiTheme';
import { MDialog, MTextField } from './NaomiUi';
import { useOidcAccessToken } from '@axa-fr/react-oidc';
import { getCatalogueImages, getCatalogueCategories } from './services/CatalogueService';
import type { CatalogueImage } from './services/CatalogueService';

// ─── Helpers (duplicated from CataloguePane to keep this component self-contained) ──

function typeLabel(typeValue: string, allTypes: string[]): string {
  const parent = allTypes.find(p => p !== typeValue && typeValue.startsWith(p + ' '));
  return parent ? `${parent} - ${typeValue.slice(parent.length + 1)}` : typeValue;
}


// ─── CataloguePicker ──────────────────────────────────────────────────────────

export function CataloguePicker({ open, onClose, onSelect }: {
  open: boolean;
  onClose: () => void;
  onSelect: (image: CatalogueImage) => void;
}): JSX.Element {
  const { accessToken } = useOidcAccessToken();
  const [images,     setImages]     = React.useState<CatalogueImage[]>([]);
  const [tradeTypes, setTradeTypes] = React.useState<string[]>([]);
  const [loading,    setLoading]    = React.useState(false);
  const [error,      setError]      = React.useState<string | null>(null);
  const [search,     setSearch]     = React.useState('');
  const [catFilter,  setCatFilter]  = React.useState<string>('');

  React.useEffect(() => {
    if (!open || !accessToken) return;
    setLoading(true);
    setError(null);
    setSearch('');
    setCatFilter('');
    Promise.all([
      getCatalogueImages(accessToken),
      getCatalogueCategories(accessToken),
    ]).then(([imgs, types]) => {
      setImages(imgs);
      setTradeTypes(types);
    }).catch(err => {
      console.error('CataloguePicker load failed', err);
      setError('Failed to load catalogue. Please try again.');
    }).finally(() => {
      setLoading(false);
    });
  }, [open, accessToken]);

  const filtered = React.useMemo(() => {
    let result = images;
    if (catFilter) {
      result = result.filter(img =>
        (img.category || '').split(',').map(s => s.trim()).includes(catFilter)
      );
    }
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter(img =>
        img.name.toLowerCase().includes(q) ||
        (img.category || '').toLowerCase().includes(q)
      );
    }
    return result;
  }, [images, catFilter, search]);

  // Category counts (only from currently search-matched images, ignoring catFilter)
  const searchFiltered = React.useMemo(() => {
    if (!search.trim()) return images;
    const q = search.trim().toLowerCase();
    return images.filter(img =>
      img.name.toLowerCase().includes(q) ||
      (img.category || '').toLowerCase().includes(q)
    );
  }, [images, search]);

  const countFor = (type: string) =>
    searchFiltered.filter(img =>
      (img.category || '').split(',').map(s => s.trim()).includes(type)
    ).length;

  return (
    <MDialog id="naomi-catalogue-picker-dialog" open={open} onClose={onClose}
      title="Pick from catalogue" icon={<NI.Camera />} width={680}
      actions={<NMButton variant="text" color="neutral" onClick={onClose}>Cancel</NMButton>}>

      {/* Search */}
      <div style={{ marginBottom: 10 }}>
        <MTextField placeholder="Search images…" value={search} onChange={setSearch}
          size="sm" adornment={<NI.Search />} />
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: NT.ink4, fontSize: 13 }}>
          Loading…
        </div>
      ) : error ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#c0392b', fontSize: 13 }}>
          {error}
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 0, minHeight: 340 }}>

          {/* ── Category sidebar ── */}
          {tradeTypes.length > 0 && (
            <div style={{
              width: 160, flexShrink: 0, borderRight: `1px solid ${NT.line}`,
              overflowY: 'auto', paddingRight: 4,
            }}>
              {/* "All" row */}
              <CategoryRow
                label="All"
                count={searchFiltered.length}
                selected={catFilter === ''}
                onClick={() => setCatFilter('')}
              />
              <div style={{ height: 1, background: NT.line, margin: '4px 0' }} />

              {/* One row per trade type */}
              {tradeTypes.map(type => {
                const count = countFor(type);
                return (
                  <CategoryRow
                    key={type}
                    label={typeLabel(type, tradeTypes)}
                    count={count}
                    selected={catFilter === type}
                    dimmed={count === 0}
                    onClick={() => setCatFilter(prev => prev === type ? '' : type)}
                  />
                );
              })}
            </div>
          )}

          {/* ── Image grid ── */}
          <div style={{ flex: 1, minWidth: 0, paddingLeft: tradeTypes.length > 0 ? 12 : 0 }}>
            {filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: NT.ink4, fontSize: 13 }}>
                {images.length === 0
                  ? 'No images in catalogue yet. Upload images from the Catalogue tab first.'
                  : 'No images match your filter.'}
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))',
                gap: 8, maxHeight: 380, overflowY: 'auto',
              }}>
                {filtered.map(img => {
                  const thumb = img.thumbnailPreviewUrl || img.previewUrl;
                  const cats  = (img.category || '').split(',').map(s => s.trim()).filter(Boolean);
                  return (
                    <div key={img.id}
                      onClick={() => { onSelect(img); onClose(); }}
                      title={img.name}
                      style={{
                        cursor: 'pointer', borderRadius: 6, overflow: 'hidden',
                        border: `1px solid ${NT.line}`, transition: 'all .1s',
                        background: NT.surface,
                      }}
                      onMouseEnter={e => {
                        (e.currentTarget as HTMLDivElement).style.borderColor = NT.primary;
                        (e.currentTarget as HTMLDivElement).style.boxShadow = NT.shadow2;
                      }}
                      onMouseLeave={e => {
                        (e.currentTarget as HTMLDivElement).style.borderColor = NT.line;
                        (e.currentTarget as HTMLDivElement).style.boxShadow = 'none';
                      }}>

                      {/* Thumbnail */}
                      <div style={{
                        height: 80, background: NT.surfaceAlt, overflow: 'hidden',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        {thumb
                          ? <img src={thumb} alt={img.name}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          : <span style={{ color: NT.ink4, opacity: 0.4 }}><NI.Camera /></span>
                        }
                      </div>

                      {/* Name */}
                      <div style={{
                        padding: '4px 6px 2px', fontSize: 10.5, color: NT.ink3,
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                        {img.name}
                      </div>

                      {/* Category chips */}
                      {cats.length > 0 && (
                        <div style={{ padding: '0 6px 5px', display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                          {cats.slice(0, 2).map((t, i) => (
                            <span key={i} style={{
                              fontSize: 9.5, padding: '1px 5px', borderRadius: 10,
                              background: t === catFilter ? NT.primarySoft : NT.surfaceAlt,
                              color: t === catFilter ? NT.primary : NT.ink4,
                              border: `1px solid ${t === catFilter ? NT.primary : NT.line}`,
                              whiteSpace: 'nowrap', maxWidth: 90,
                              overflow: 'hidden', textOverflow: 'ellipsis',
                              display: 'inline-block',
                            }}>
                              {typeLabel(t, tradeTypes)}
                            </span>
                          ))}
                          {cats.length > 2 && (
                            <span style={{ fontSize: 9.5, color: NT.ink4 }}>+{cats.length - 2}</span>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </MDialog>
  );
}

// ─── CategoryRow ──────────────────────────────────────────────────────────────

function CategoryRow({ label, count, selected, dimmed, onClick }: {
  label: string;
  count: number;
  selected: boolean;
  dimmed?: boolean;
  onClick: () => void;
}) {
  const [hover, setHover] = React.useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '6px 8px', borderRadius: 6, cursor: 'pointer',
        background: selected ? NT.primarySoft : hover ? NT.surfaceAlt : 'transparent',
        color: selected ? NT.primary : dimmed ? NT.ink4 : NT.ink,
        opacity: dimmed ? 0.5 : 1,
        transition: 'background 0.1s',
      }}>
      <span style={{
        fontSize: 12, fontWeight: selected ? 600 : 400,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1,
      }}>
        {label}
      </span>
      <span style={{
        fontSize: 10.5, color: selected ? NT.primary : NT.ink4,
        background: NT.surfaceAlt, borderRadius: 10,
        padding: '0 5px', marginLeft: 4, flexShrink: 0,
      }}>
        {count}
      </span>
    </div>
  );
}
